/**
 * turnAttribution.ts — ONE ⓘ PER TURN (owner directive 2026-09-01).
 *
 * The Connect-Fitbit repro: a turn answered with a short bubble AND posted a
 * connect tile; both rendered the ⓘ model-info icon, and — since the
 * 2026-08-29 series stamp copies the answer's token-usage lineup onto every
 * card of the turn — both popups listed the identical model/tool series. To
 * the user that reads as "the model ran twice for one request". It did not:
 * the card's usage is an INHERITED COPY (ChatMessage.tokenUsageInherited).
 *
 * Rule: a turn's compute is attributed exactly once, on the answer bubble
 * that produced the series. A card in the same turn shows no ⓘ while that
 * bubble is present. When the card IS the turn's only surface (no visible
 * answer bubble — e.g. the primary's whole answer was the card), the card
 * keeps the ⓘ with the stamped series, so the 2026-08-29 truthfulness fix
 * ("no extensive compute was necessary" on a turn that ran the whole series)
 * is preserved. Cards whose usage is their own (a structured-event render,
 * not flagged inherited) are never suppressed.
 *
 * Pure. The "turn" is the same speaker run the transcript already groups on
 * (messageTurns.messageTurnKey): scan out from the card while the speaker
 * stays Prest0n; the window closes at the first non-assistant row. Prest0n
 * has TWO turn-key forms — bare `assistant` in solo threads (no senderId) and
 * `participant:assistant` in group threads, where every AI row carries the
 * ASSISTANT_SENDER_ID sentinel (review finding, PR #1381: comparing against
 * the bare form alone meant the rule never fired in a group thread).
 */
import type { ChatMessage } from './types';
import { messageTurnKey } from './messageTurns';

/**
 * Mirror of ASSISTANT_SENDER_ID (src/types/groupThreadMessageDoc.ts) — kept
 * local like mentionAffordance/summonGate/followupVisibility do, so this pure
 * module never imports the Firestore doc layer. Pinned by a lockstep test.
 */
const ASSISTANT_SENDER_ID = 'assistant';
const ASSISTANT_PARTICIPANT_TURN_KEY = `participant:${ASSISTANT_SENDER_ID}`;

/** True for either form of Prest0n's turn key (solo or group thread). */
export function isAssistantTurnKey(turnKey: string): boolean {
  return turnKey === 'assistant' || turnKey === ASSISTANT_PARTICIPANT_TURN_KEY;
}

/**
 * The renderer's special-tile predicate, shared so the attribution rule and
 * the dispatch site can never disagree about what a "card" is.
 * ANSWER-FIRST (2026-08-20): the two DR card sourceTags and the translation
 * result are tile-owned shapes regardless of button presence.
 */
export function isSpecialTileMessage(message: ChatMessage | null | undefined): boolean {
  if (!message) return false;
  return Boolean(
    (message.actionButtons && message.actionButtons.length > 0) ||
      message.messageType ||
      message.title ||
      !!message.workflowActionButtonsHtml ||
      (!message.isUser &&
        !!message.html &&
        (message.sourceTag === 'deep_research_qualification' ||
          message.sourceTag === 'deep_research_answer' ||
          message.sourceTag === 'translation_result')),
  );
}

function carriesUsage(message: ChatMessage): boolean {
  if (message.tokenUsages && message.tokenUsages.length > 0) return true;
  if (message.tokenUsageSnapshots && message.tokenUsageSnapshots.length > 0) return true;
  return !!(message.tokenUsage || message.tokenUsageSnapshot);
}

/**
 * True when the card at `index` should render WITHOUT its ⓘ because an
 * answer bubble earlier in the same assistant run owns the turn's series.
 */
export function cardAttributionOwnedByTurnAnswer(
  messages: ReadonlyArray<ChatMessage>,
  card: ChatMessage | null | undefined,
  currentUid?: string | null,
): boolean {
  if (!card || card.isUser) return false;
  // Locate the card in the transcript by identity, then by id (the renderer
  // may hand a spread copy). Cards are few; the scan is cheap.
  let index = messages.indexOf(card);
  if (index < 0) index = messages.findIndex((m) => !!m && m.id === card.id);
  if (index < 0) return false;
  // Only an inherited copy defers; a card with its own usage keeps its ⓘ.
  if (card.tokenUsageInherited !== true) return false;
  if (!isAssistantTurnKey(messageTurnKey(card, currentUid))) return false;
  // The turn is the contiguous ASSISTANT run around the card, scanned in BOTH
  // directions: a card usually follows its answer bubble, but on reload the
  // transcript orders by timestamp and a card posted mid-turn can sort ahead
  // of a bubble stamped at finalize time (review candidate, PR #1381). Either
  // ordering is one turn; the bubble owns the ⓘ in both.
  const ownsAttribution = (m: ChatMessage | undefined): boolean => {
    if (!m) return false;
    if (isSpecialTileMessage(m)) return false; // sibling cards never own attribution
    if (m.isContextOnly) return false; // silent data-only records are not visible bubbles
    if (m.interrupted || m.status === 'interrupted') return false;
    const hasBody = !!(m.text && m.text.trim()) || !!(m.html && m.html.trim());
    return hasBody && carriesUsage(m);
  };
  for (let i = index - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (!m) continue;
    if (!isAssistantTurnKey(messageTurnKey(m, currentUid))) break; // window closed
    if (ownsAttribution(m)) return true;
  }
  for (let i = index + 1; i < messages.length; i += 1) {
    const m = messages[i];
    if (!m) continue;
    if (!isAssistantTurnKey(messageTurnKey(m, currentUid))) break; // window closed
    if (ownsAttribution(m)) return true;
  }
  return false;
}
